import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Login from "./components/Login";
import EmailVerification from "./components/EmailVerification";
import PhoneVerification from "./components/PhoneVerification";
import FirstTimePasswordReset from "./components/FirstTimePasswordReset";
import Dashboard from "./components/Dashboard";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useActivityMonitor } from "./hooks/useActivityMonitor";
import { useAccessControl } from "./hooks/useAccessControl";
import { RESOURCES, ACTIONS } from "./hooks/usePermissions";
import { useFirstAccessibleRoute } from "./hooks/useFirstAccessibleRoute";
import NotFoundPage from "./components/common/NotFoundPage";
import RegisterSchool from "./components/RegisterSchool";
import SchoolSuspended from "./components/SchoolSuspended";
import CentralAdminPanel from "./components/admin/CentralAdminPanel";

import Students from "./components/student/Students";
import StudentLedger from "./components/student/StudentLedger";
import StudentPromotionPage from "./components/student/StudentPromotionPage";
import StudentBulkStatusPage from "./components/student/StudentBulkStatusPage";

import FeeTypes from "./components/fees/FeeTypes";
import FeeGroups from "./components/fees/FeeGroups";
import FeeAllocate from "./components/fees/FeeAllocate";
import FeePayments from "./components/fees/FeePayments";
import PaymentsTransactions from "./components/fees/PaymentsTransactions";
import ClearanceSettings from "./components/fees/ClearanceSettings";
import ClearanceCardGeneration from "./components/fees/ClearanceCardGeneration";

import Reports from "./components/fees/Reports";
import CoaAccounts from "./components/accounting/CoaAccounts";
import FiscalPeriods from "./components/accounting/FiscalPeriods";
import AccountingSettings from "./components/accounting/AccountingSettings";
import Journals from "./components/accounting/Journals";
import AccountBalances from "./components/accounting/AccountBalances";
import CashBank from "./components/accounting/CashBank";
import StudentReceivables from "./components/accounting/StudentReceivables";
import FeesBilling from "./components/accounting/FeesBilling";
import AccountsPayable from "./components/accounting/AccountsPayable";
import Income from "./components/accounting/Income";
import Accruals from "./components/accounting/Accruals";
import Projects from "./components/accounting/Projects";
import AuditTrail from "./components/accounting/AuditTrail";
import Costing from "./components/accounting/Costing";
import Expenses from "./components/accounting/Expenses";
import Budgeting from "./components/accounting/Budgeting";
import Assets from "./components/accounting/Assets";
import Inventory from "./components/accounting/Inventory";
import Tax from "./components/accounting/Tax";
import Recon from "./components/accounting/Recon";
import LedgerReports from "./components/accounting/LedgerReports";
import AccountingDashboard from "./components/accounting/AccountingDashboard";
import EmailLogs from "./components/communications/EmailLogs";
import EmailManagement from "./components/communications/EmailManagement";
import Communications from "./components/communications/Communications";
import SMSPage from "./components/communications/SMSPage";
import Teachers from "./components/teachers/Teachers";
import ClassAllocations from "./components/class-allocations/ClassAllocations";
import SubjectAllocations from "./components/subject-allocations/SubjectAllocations";
import AcademicClasses from "./components/academic/AcademicClasses";
import AcademicTerms from "./components/academic/AcademicTerms";
import AcademicLevels from "./components/academic/AcademicLevels";
import Subjects from "./components/academic/Subjects";
import SubjectPapers from "./components/academic/SubjectPapers";
import SubjectWithPapers from "./components/academic/SubjectWithPapers";
import ClassStreamSubjectConfig from "./components/academic/ClassStreamSubjectConfig";
import RBACPage from "./components/rbac/RBACPage";
import UserManagement from "./components/settings/UserManagement";
import AccountSettings from "./components/settings/AccountSettings";
import Roles from "./components/settings/Roles";
import SchoolSettings from "./components/settings/SchoolSettings";
import AssessmentTypes from "./components/assessments/AssessmentTypes";
import AssessmentConfigurations from "./components/assessments/AssessmentConfigurations";
import StudentMarks from "./components/assessments/StudentMarks";
import AssessmentReports from "./components/assessments/AssessmentReports";
import TeacherWorkload from "./components/assessments/TeacherWorkload";
import GradingScales from "./components/grading/GradingScales";
import GradeBoundaries from "./components/grading/GradeBoundaries";
import ClassGradingScales from "./components/grading/ClassGradingScales";
import ReportCardHub from "./components/report-card/ReportCardHub";
import ConductManagement from "./components/report-card/ConductManagement";
import CommentsManagement from "./components/report-card/CommentsManagement";
import ConductCategories from "./components/report-card/ConductCategories";
import ConductRatings from "./components/report-card/ConductRatings";
import StudentConduct from "./components/report-card/StudentConduct";
import SubjectComments from "./components/report-card/SubjectComments";
import GeneralComments from "./components/report-card/GeneralComments";
import ReportCardGeneration from "./components/report-card/ReportCardGeneration";
import CommentTemplates from "./components/report-card/CommentTemplates";
import ReportCardTemplates from "./components/report-card/ReportCardTemplates";
// TODO: Uncomment when PrimaryPreferences component is developed
// import PrimaryPreferences from "./components/report-card/PrimaryPreferences";
import Layout from "./components/Layout";
import StudentLogin from "./components/student-portal/StudentLogin";
import StudentDashboard from "./components/student-portal/StudentDashboard";
import StudentChangePassword from "./components/student-portal/StudentChangePassword";
import { StudentAuthProvider, useStudentAuth } from "./hooks/useStudentAuth";
import { SchoolPayTransactions, SchoolPayAdhocPayments } from "./components/schoolpay";

const PRIMARY_COLOR = "#2e7d32";
const PRIMARY_COLOR_DARK = "#1b5e20";
const SCROLLBAR_TRACK_COLOR = "#f1f1f1";

const theme = createTheme({
  palette: {
    primary: {
      main: PRIMARY_COLOR,
    },
    secondary: {
      main: "#f50057",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    h1: {
      fontSize: "1.8rem",
      "@media (max-width:600px)": { fontSize: "1.4rem" },
    },
    h2: {
      fontSize: "1.6rem",
      "@media (max-width:600px)": { fontSize: "1.2rem" },
    },
    h3: {
      fontSize: "1.4rem",
      "@media (max-width:600px)": { fontSize: "1.1rem" },
    },
    h4: {
      fontSize: "1.2rem",
      "@media (max-width:600px)": { fontSize: "1rem" },
    },
    h5: {
      fontSize: "1rem",
      "@media (max-width:600px)": { fontSize: "0.9rem" },
    },
    h6: {
      fontSize: "0.9rem",
      "@media (max-width:600px)": { fontSize: "0.8rem" },
    },
    body1: {
      fontSize: "0.8rem",
      "@media (max-width:600px)": { fontSize: "0.75rem" },
    },
    body2: {
      fontSize: "0.75rem",
      "@media (max-width:600px)": { fontSize: "0.7rem" },
    },
    button: {
      fontSize: "0.8rem",
      "@media (max-width:600px)": { fontSize: "0.75rem" },
    },
    caption: {
      fontSize: "0.7rem",
      "@media (max-width:600px)": { fontSize: "0.65rem" },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        // Slim, theme-colored scrollbars app-wide (Firefox)
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: `${PRIMARY_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
        },
        // Slim, theme-colored scrollbars app-wide (Chrome/Edge/Safari)
        "*::-webkit-scrollbar": {
          width: 6,
          height: 6,
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: SCROLLBAR_TRACK_COLOR,
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: PRIMARY_COLOR,
          borderRadius: 6,
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: PRIMARY_COLOR_DARK,
        },
        "*::-webkit-scrollbar-corner": {
          backgroundColor: SCROLLBAR_TRACK_COLOR,
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: "xl",
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          "@media (max-width:600px)": {
            margin: "0 4px",
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          fontFamily: '"Avenir LT Pro", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          "@media (max-width:900px)": {
            overflowX: "auto",
            "& .MuiTable-root": {
              minWidth: 650,
            },
          },
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({
  children,
  requiredPermission = null,
  requiredPermissions = [],
  requireAll = true,
}) => {
  const { user, loading, canAccess, canAny, canAll } = useAccessControl();
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !canAccess(requiredPermission)) {
    return <Navigate to="/404" replace />;
  }

  if (requiredPermissions.length > 0) {
    const isAllowed = requireAll ? canAll(requiredPermissions) : canAny(requiredPermissions);
    if (!isAllowed) {
      return <Navigate to="/404" replace />;
    }
  }
  
  return children;
};

// Dashboard guard — redirects to first accessible route if no dashboard read
const DashboardRoute = () => {
  const { can, loading } = useAccessControl();
  const firstRoute = useFirstAccessibleRoute();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!can(RESOURCES.DASHBOARD, ACTIONS.READ)) {
    return <Navigate to={firstRoute} replace />;
  }

  return (
    <LayoutWrapper>
      <Dashboard />
    </LayoutWrapper>
  );
};

const LayoutWrapper = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Map routes to page names for the sidebar
  const getPageFromPath = (path) => {
    const routeMap = {
      "/": "dashboard",
      "/students": "students",
      "/teachers": "teachers",
      "/fees/types": "fees-types",
      "/fees/groups": "fees-groups",
      "/fees/allocate": "fees-allocate",
      "/fees/transactions": "fees-transactions-all",
      "/fees/payments": "fees-payments",
      "/fees/reports": "fees-reports",
      "/fees/clearance-settings": "fees-clearance-settings",
      "/fees/clearance-cards": "fees-clearance-cards",
      "/accounting/transactions": "accounting-transactions",
      "/ledger/dashboard": "ledger-dashboard",
      "/ledger/coa": "ledger-coa",
      "/ledger/gl": "ledger-gl",
      "/ledger/accruals": "ledger-accruals",
      "/ledger/projects": "ledger-projects",
      "/ledger/audit": "ledger-audit",
      "/ledger/costing": "ledger-costing",
      "/ledger/balances": "ledger-balances",
      "/ledger/cash": "ledger-cash",
      "/ledger/receivables": "ledger-receivables",
      "/ledger/fees": "ledger-fees",
      "/ledger/ap": "ledger-ap",
      "/ledger/income": "ledger-income",
      "/ledger/expenses": "ledger-expenses",
      "/ledger/budgeting": "ledger-budgeting",
      "/ledger/assets": "ledger-assets",
      "/ledger/inventory": "ledger-inventory",
      "/ledger/tax": "ledger-tax",
      "/ledger/recon": "ledger-recon",
      "/ledger/reports": "ledger-reports",
      "/ledger/close": "ledger-close",
      "/ledger/settings": "ledger-settings",
      "/accounting/dashboard": "accounting-dashboard",
      "/accounting/accounts": "accounting-accounts",
      "/accounting/voucher-heads": "accounting-voucher-heads",
      "/academic/classes": "academic-classes",
      "/academic/terms": "academic-terms",
      "/academic/levels": "academic-levels",
      "/academic/subjects": "academic-subjects",
      "/academic/subject-with-papers": "academic-subject-with-papers",
      "/academic/subject-papers": "academic-subject-papers",
      "/academic/curriculum": "academic-curriculum",
      "/academic/class-allocations": "academic-class-allocations",
      "/academic/subject-allocations": "academic-subject-allocations",
      "/communications": "communications",
      "/communications/logs": "communications-logs",
      "/communications/emails": "communications-email-management",
      "/communications/sms": "communications-sms",
      "/teachers": "teachers",
      "/teachers/class-allocations": "teachers-class-allocations",
      "/teachers/subject-allocations": "teachers-subject-allocations",
      "/settings/users": "settings-users",
      "/settings/roles": "settings-roles",
      "/settings/school": "settings-school",
      "/settings/account": "settings-account",
      "/settings/rbac": "settings-rbac",
      "/assessments/types": "assessments-types",
      "/assessments/configurations": "assessments-configurations",
      "/assessments/marks": "assessments-marks",
      "/assessments/reports": "assessments-reports",
      "/assessments/teacher-workload": "assessments-teacher-workload",
      "/grading/scales": "grading-scales",
      "/grading/boundaries": "grading-boundaries",
      "/grading/class-scales": "grading-class-scales",
      "/report-card/hub": "report-card-hub",
      "/report-card/conduct": "report-card-conduct",
      "/report-card/comments": "report-card-comments",
      "/report-card/conduct-categories": "report-card-conduct-categories",
      "/report-card/conduct-ratings": "report-card-conduct-ratings",
      "/report-card/student-conduct": "report-card-student-conduct",
      "/report-card/subject-comments": "report-card-subject-comments",
      "/report-card/general-comments": "report-card-general-comments",
      "/report-card/generation": "report-card-generation",
      "/report-card/comment-templates": "report-card-comment-templates",
      "/report-card/templates": "report-card-templates",
      // "/report-card/primary-preferences": "report-card-primary-preferences", // TODO: Uncomment when PrimaryPreferences component is developed
      "/schoolpay/transactions": "schoolpay-transactions",
      "/schoolpay/adhoc": "schoolpay-adhoc",
    };
    return routeMap[path] || "dashboard";
  };

  const handlePageChange = (pageName) => {
    const pageMap = {
      dashboard: "/",
      students: "/students",
      teachers: "/teachers",
      "fees-types": "/fees/types",
      "fees-groups": "/fees/groups",
      "fees-allocate": "/fees/allocate",
      "fees-transactions-all": "/fees/transactions",
      "fees-payments": "/fees/payments",
      "fees-reports": "/fees/reports",
      "fees-clearance-settings": "/fees/clearance-settings",
      "fees-clearance-cards": "/fees/clearance-cards",
      "accounting-transactions": "/accounting/transactions",
      "ledger-dashboard": "/ledger/dashboard",
      "ledger-coa": "/ledger/coa",
      "ledger-gl": "/ledger/gl",
      "ledger-accruals": "/ledger/accruals",
      "ledger-projects": "/ledger/projects",
      "ledger-audit": "/ledger/audit",
      "ledger-costing": "/ledger/costing",
      "ledger-balances": "/ledger/balances",
      "ledger-cash": "/ledger/cash",
      "ledger-receivables": "/ledger/receivables",
      "ledger-fees": "/ledger/fees",
      "ledger-ap": "/ledger/ap",
      "ledger-income": "/ledger/income",
      "ledger-expenses": "/ledger/expenses",
      "ledger-budgeting": "/ledger/budgeting",
      "ledger-assets": "/ledger/assets",
      "ledger-inventory": "/ledger/inventory",
      "ledger-tax": "/ledger/tax",
      "ledger-recon": "/ledger/recon",
      "ledger-reports": "/ledger/reports",
      "ledger-close": "/ledger/close",
      "ledger-settings": "/ledger/settings",
      "accounting-dashboard": "/accounting/dashboard",
      "accounting-accounts": "/accounting/accounts",
      "accounting-voucher-heads": "/accounting/voucher-heads",
      "academic-classes": "/academic/classes",
      "academic-terms": "/academic/terms",
      "academic-levels": "/academic/levels",
      "academic-subjects": "/academic/subjects",
      "academic-subject-with-papers": "/academic/subject-with-papers",
      "academic-subject-papers": "/academic/subject-papers",
      "academic-curriculum": "/academic/curriculum",
      "academic-class-allocations": "/academic/class-allocations",
      "academic-subject-allocations": "/academic/subject-allocations",
      communications: "/communications",
      "communications-logs": "/communications/logs",
      "communications-email-management": "/communications/emails",
      "communications-sms": "/communications/sms",
      teachers: "/teachers",
      "teachers-class-allocations": "/teachers/class-allocations",
      "teachers-subject-allocations": "/teachers/subject-allocations",
      "settings-users": "/settings/users",
      "settings-roles": "/settings/roles",
      "settings-school": "/settings/school",
      "settings-account": "/settings/account",
      "settings-rbac": "/settings/rbac",
      "assessments-types": "/assessments/types",
      "assessments-configurations": "/assessments/configurations",
      "assessments-marks": "/assessments/marks",
      "assessments-reports": "/assessments/reports",
      "assessments-teacher-workload": "/assessments/teacher-workload",
      "grading-scales": "/grading/scales",
      "grading-boundaries": "/grading/boundaries",
      "grading-class-scales": "/grading/class-scales",
      "report-card-hub": "/report-card/hub",
      "report-card-conduct": "/report-card/conduct",
      "report-card-comments": "/report-card/comments",
      "report-card-conduct-categories": "/report-card/conduct-categories",
      "report-card-conduct-ratings": "/report-card/conduct-ratings",
      "report-card-student-conduct": "/report-card/student-conduct",
      "report-card-subject-comments": "/report-card/subject-comments",
      "report-card-general-comments": "/report-card/general-comments",
      "report-card-generation": "/report-card/generation",
      "report-card-comment-templates": "/report-card/comment-templates",
      "report-card-templates": "/report-card/templates",
      // "report-card-primary-preferences": "/report-card/primary-preferences", // TODO: Uncomment when PrimaryPreferences component is developed
      "schoolpay-transactions": "/schoolpay/transactions",
      "schoolpay-adhoc": "/schoolpay/adhoc",
    };
    navigate(pageMap[pageName] || "/");
  };

  return (
    <Layout
      user={user}
      onLogout={logout}
      currentPage={getPageFromPath(window.location.pathname)}
      setCurrentPage={handlePageChange}
    >
      {children}
    </Layout>
  );
};

function AppContent() {
  const { user, loading, login: authLogin, logout, suspended } = useAuth();
  const navigate = useNavigate();
  
  useActivityMonitor();

  // Redirect to suspended page if school is suspended on app load
  useEffect(() => {
    if (suspended) navigate('/suspended', { replace: true });
  }, [suspended, navigate]);

  const handleLogin = (userData) => {
    authLogin(userData.user, userData.token, userData.permissions, userData.expires_at, userData.tenant_id);
    navigate('/');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        }
      />
      
      <Route path="/admin/*" element={<CentralAdminPanel />} />

      <Route
        path="/register"
        element={
          user ? <Navigate to="/" replace /> : <RegisterSchool />
        }
      />

      <Route
        path="/verify-email"
        element={<EmailVerification onLogin={handleLogin} />}
      />
      
      <Route
        path="/verify-phone"
        element={<PhoneVerification onLogin={handleLogin} />}
      />
      
      <Route
        path="/first-time-reset"
        element={<FirstTimePasswordReset onPasswordReset={handleLogin} />}
      />

      <Route path="/suspended" element={<SchoolSuspended />} />

      <Route path="/404" element={<NotFoundPage />} />

      {/* Student Portal */}
      <Route path="/student/login" element={<StudentLogin />} />
      <Route path="/student/dashboard" element={<StudentProtectedRoute><StudentDashboard /></StudentProtectedRoute>} />
      <Route path="/student/change-password" element={<StudentProtectedRoute><StudentChangePassword /></StudentProtectedRoute>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />

      <Route path="/students" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.READ }}><LayoutWrapper><Students /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/promote" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.UPDATE }}><LayoutWrapper><StudentPromotionPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/deactivate" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.UPDATE }}><LayoutWrapper><StudentBulkStatusPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/students/ledger/:studentId" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.STUDENTS, action: ACTIONS.READ }}><LayoutWrapper><StudentLedger /></LayoutWrapper></ProtectedRoute>} />

      <Route path="/fees/types" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.TYPES, action: ACTIONS.READ }}><LayoutWrapper><FeeTypes /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/groups" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.GROUPS, action: ACTIONS.READ }}><LayoutWrapper><FeeGroups /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/allocate" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><FeeAllocate /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/transactions" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.PAYMENTS, action: ACTIONS.READ }}><LayoutWrapper><PaymentsTransactions /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/payments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.PAYMENTS, action: ACTIONS.READ }}><LayoutWrapper><FeePayments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/reports" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><Reports /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/clearance-settings" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.CLEARANCE, action: ACTIONS.READ }}><LayoutWrapper><ClearanceSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/fees/clearance-cards" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.CLEARANCE, action: ACTIONS.READ }}><LayoutWrapper><ClearanceCardGeneration /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/accounting/dashboard" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACCOUNTING.TRANSACTIONS, action: ACTIONS.READ }}><LayoutWrapper><AccountingDashboard /></LayoutWrapper></ProtectedRoute>} />
      {/* Ledger-backed accounting (Phase 1 live, rest land per phase) */}
      <Route path="/ledger/coa" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.COA, action: ACTIONS.READ }}><LayoutWrapper><CoaAccounts /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/gl" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.GL, action: ACTIONS.READ }}><LayoutWrapper><Journals /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/accruals" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.GL, action: ACTIONS.READ }}><LayoutWrapper><Accruals /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/projects" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.BUDGETING, action: ACTIONS.READ }}><LayoutWrapper><Projects /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/audit" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><AuditTrail /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/costing" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.BUDGETING, action: ACTIONS.READ }}><LayoutWrapper><Costing /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/balances" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.GL, action: ACTIONS.READ }}><LayoutWrapper><AccountBalances /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/close" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.CLOSE, action: ACTIONS.READ }}><LayoutWrapper><FiscalPeriods /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/settings" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.SETTINGS, action: ACTIONS.READ }}><LayoutWrapper><AccountingSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/reports" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><LedgerReports /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/dashboard" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.DASHBOARD, action: ACTIONS.READ }}><LayoutWrapper><AccountingDashboard /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/cash" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.CASH, action: ACTIONS.READ }}><LayoutWrapper><CashBank /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/receivables" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.RECEIVABLES, action: ACTIONS.READ }}><LayoutWrapper><StudentReceivables /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/fees" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.FEES, action: ACTIONS.READ }}><LayoutWrapper><FeesBilling /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/ap" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.AP, action: ACTIONS.READ }}><LayoutWrapper><AccountsPayable /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/income" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.INCOME, action: ACTIONS.READ }}><LayoutWrapper><Income /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/expenses" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.EXPENSES, action: ACTIONS.READ }}><LayoutWrapper><Expenses /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/budgeting" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.BUDGETING, action: ACTIONS.READ }}><LayoutWrapper><Budgeting /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/assets" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.ASSETS, action: ACTIONS.READ }}><LayoutWrapper><Assets /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/inventory" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.INVENTORY, action: ACTIONS.READ }}><LayoutWrapper><Inventory /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/tax" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.TAX, action: ACTIONS.READ }}><LayoutWrapper><Tax /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/ledger/recon" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.LEDGER.RECON, action: ACTIONS.READ }}><LayoutWrapper><Recon /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/accounting/accounts" element={<Navigate to="/ledger/coa" replace />} />
      <Route path="/accounting/voucher-heads" element={<Navigate to="/ledger/settings" replace />} />
      <Route path="/academic/classes" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.CLASSES, action: ACTIONS.READ }}><LayoutWrapper><AcademicClasses /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/terms" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.TERMS, action: ACTIONS.READ }}><LayoutWrapper><AcademicTerms /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/levels" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ACADEMIC.LEVELS, action: ACTIONS.READ }}><LayoutWrapper><AcademicLevels /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subjects" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECTS, action: ACTIONS.READ }}><LayoutWrapper><Subjects /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-papers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><SubjectPapers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-with-papers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><SubjectWithPapers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/curriculum" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CLASS_STREAM_PAPERS, action: ACTIONS.READ }}><LayoutWrapper><ClassStreamSubjectConfig /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/class-allocations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CLASS_TEACHER_ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><ClassAllocations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/academic/subject-allocations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_TEACHER_ALLOCATIONS, action: ACTIONS.READ }}><LayoutWrapper><SubjectAllocations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><Communications /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/logs" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><EmailLogs /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/emails" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.COMMUNICATIONS.EMAIL, action: ACTIONS.SEND }}><LayoutWrapper><EmailManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/communications/sms" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.READ }, { resource: RESOURCES.COMMUNICATIONS.SMS, action: ACTIONS.SEND }]} requireAll={false}><LayoutWrapper><SMSPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/teachers" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.TEACHERS, action: ACTIONS.READ }}><LayoutWrapper><Teachers /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/users" element={<ProtectedRoute requiredPermission={{ resource: 'users', action: 'read' }}><LayoutWrapper><UserManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/roles" element={<ProtectedRoute requiredPermission={{ resource: 'roles', action: 'read' }}><LayoutWrapper><Roles /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/school" element={<ProtectedRoute><LayoutWrapper><SchoolSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><LayoutWrapper><AccountSettings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/settings/rbac" element={<ProtectedRoute requiredPermissions={[{ resource: 'permissions', action: 'read' }, { resource: 'users', action: 'read' }]}><LayoutWrapper><RBACPage /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/types" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.TYPES, action: ACTIONS.READ }}><LayoutWrapper><AssessmentTypes /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/configurations" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.CONFIGURATIONS, action: ACTIONS.READ }}><LayoutWrapper><AssessmentConfigurations /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/marks" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.MARKS, action: ACTIONS.READ }}><LayoutWrapper><StudentMarks /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/reports" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><AssessmentReports /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/assessments/teacher-workload" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.ASSESSMENTS.REPORTS, action: ACTIONS.READ }}><LayoutWrapper><TeacherWorkload /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/scales" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><GradingScales /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/boundaries" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><GradeBoundaries /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/grading/class-scales" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GRADING, action: ACTIONS.READ }}><LayoutWrapper><ClassGradingScales /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/hub" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.REPORT_CARDS, action: ACTIONS.READ }}><LayoutWrapper><ReportCardHub /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/comments" element={<ProtectedRoute requiredPermissions={[{ resource: RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, action: ACTIONS.READ }, { resource: RESOURCES.REPORT_CARD.GENERAL_COMMENTS, action: ACTIONS.READ }]} requireAll={false}><LayoutWrapper><CommentsManagement /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct-categories" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductCategories /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/conduct-ratings" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><ConductRatings /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/student-conduct" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.CONDUCT, action: ACTIONS.READ }}><LayoutWrapper><StudentConduct /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/subject-comments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, action: ACTIONS.READ }}><LayoutWrapper><SubjectComments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/general-comments" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.GENERAL_COMMENTS, action: ACTIONS.READ }}><LayoutWrapper><GeneralComments /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/generation" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.REPORT_CARDS, action: ACTIONS.READ }}><LayoutWrapper><ReportCardGeneration /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/comment-templates" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.COMMENT_TEMPLATES, action: ACTIONS.READ }}><LayoutWrapper><CommentTemplates /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/report-card/templates" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.TEMPLATES, action: ACTIONS.READ }}><LayoutWrapper><ReportCardTemplates /></LayoutWrapper></ProtectedRoute>} />
      {/* TODO: Uncomment when PrimaryPreferences component is developed */}
      {/* <Route path="/report-card/primary-preferences" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.REPORT_CARD.PRIMARY_PREFERENCES, action: ACTIONS.READ }}><LayoutWrapper><PrimaryPreferences /></LayoutWrapper></ProtectedRoute>} /> */}

      {/* SchoolPay Integration */}
      <Route path="/schoolpay/transactions" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.PAYMENTS, action: ACTIONS.READ }}><LayoutWrapper><SchoolPayTransactions /></LayoutWrapper></ProtectedRoute>} />
      <Route path="/schoolpay/adhoc" element={<ProtectedRoute requiredPermission={{ resource: RESOURCES.FEES.PAYMENTS, action: ACTIONS.READ }}><LayoutWrapper><SchoolPayAdhocPayments /></LayoutWrapper></ProtectedRoute>} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Guard for student portal routes
const StudentProtectedRoute = ({ children }) => {
  const { student, loading } = useStudentAuth();
  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  if (!student) return <Navigate to="/student/login" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <StudentAuthProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </StudentAuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
